// Doc-Ref: docs/features/collaborations-current.md
import PageHeader from "./Components/PageHeader/PageHeader";
import EntitySelector from "./Components/EntitySelector/EntitySelector";
import FiltersRow from "./Components/FiltersRow/FiltersRow";

import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getSuggestSearchAuthors, getAuthorProfileSummaryByID } from "Explore/Services/Author";
import { getSuggestSearchInstitutions, getInstitutionDetailSummaryByID } from "Explore/Services/Institution";
import { getSuggestSearchUnits, getUnitSummaryById } from "Explore/Services/Unit";
import useAdminInstitution from "Explore/Hooks/useAdminInstitution";

import { useQuery } from "@tanstack/react-query";
import { FilePlus, Search, Table, Loader2 } from "lucide-react";
import TabGeneral from "./Tabs/General/TabGeneral";
import TabCurrent from "./Tabs/Current/TabCurrent";
import TabPotential from "./Tabs/Potential/TabPotential";
import AddToReportModal from "Explore/Components/AddToReportModal";
import SaveViewModal from "Explore/Components/SaveViewModal/SaveViewModal";
import SavedViewsDropdown from "Explore/Components/SavedViewsDropdown/SavedViewsDropdown";
import SavedViewsList from "Explore/Components/SavedViewsList/SavedViewsList";
import EmptyStateSuggest from "./Components/EmptyState/EmptyStateSuggest";
import InsufficientDataNotice from "./Components/InsufficientDataNotice/InsufficientDataNotice";
import { getGeneralOverview, getGeneralSocialImpact } from "Explore/Services/Collaborations";
import { buildGeneralCollaborationsParams } from "./Tabs/General/utils";
import { createSavedView } from "Explore/Services/SavedViews";

import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";

const CollaborationSkeleton = () => (
  <div className="flex flex-col gap-6 w-full flex-1">
    {/* Entity Selector Skeleton */}
    <div className="flex items-center gap-3 p-3 bg-blue-50/50 border border-blue-100 rounded-lg w-full">
      <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-6 w-6 rounded" />
    </div>

    {/* Main 2-column Area Skeleton */}
    <div className="flex flex-col lg:flex-row items-start flex-1 gap-6 w-full">
      {/* Sidebar Skeleton (Filters) */}
      <div className="w-full lg:w-[280px] flex-shrink-0 bg-white border border-gray-300 rounded-lg p-5 shadow-sm space-y-5">
        <div className="flex justify-between items-center pb-2 border-b border-gray-100">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-4 w-12" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <Skeleton className="h-9 w-full rounded-md mt-4" />
      </div>

      {/* Right Content Skeleton (Tabs + Cards + Charts) */}
      <div className="flex-1 min-w-0 flex flex-col bg-white border border-gray-300 rounded-lg shadow-sm min-h-[550px] overflow-hidden">
        {/* Tabs Bar Header Skeleton */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-gray-200 px-6 py-3 gap-3">
          <div className="flex items-center gap-4">
            <Skeleton className="h-8 w-20 rounded" />
            <Skeleton className="h-8 w-36 rounded" />
            <Skeleton className="h-8 w-36 rounded" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-28 rounded" />
            <Skeleton className="h-8 w-28 rounded" />
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="p-6 space-y-6 flex-1">
          {/* KPI Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>

          {/* Charts Grid Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-5 space-y-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3.5 w-60" />
              <Skeleton className="h-64 w-full rounded-md" />
            </div>
            <div className="border border-gray-200 rounded-lg p-5 space-y-4">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-3.5 w-64" />
              <Skeleton className="h-64 w-full rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Collaborations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { institution: adminInstitution, isLoading: isAdminInstLoading } = useAdminInstitution();

  const currentYear = new Date().getFullYear();

  const [entityType, setEntityType] = useState("institution");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [filters, setFilters] = useState({
    yearRange: [2014, currentYear],
    subjectArea: [],
    geography: "us",
    metric: "publications",
    collaboratorType: "institution",
  });

  const [activeTab, setActiveTab] = useState("general");
  const [isAddToReportModalOpen, setIsAddToReportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
  const [refreshViewsTrigger, setRefreshViewsTrigger] = useState(0);
  const [searchOptions, setSearchOptions] = useState([]);
  const [isDataEmpty, setIsDataEmpty] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const searchDebounceRef = useRef(null);
  const initializedRef = useRef(false);

  const generalParams = buildGeneralCollaborationsParams(selectedEntity, filters);

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    error: overviewError,
  } = useQuery({
    queryKey: [
      'collab-general-overview',
      generalParams.entity_id,
      generalParams.entity_type,
      generalParams.publication_year_start,
      generalParams.publication_year_end,
      generalParams.keywords?.join(','),
      generalParams.keywords_operator,
      generalParams.geography,
      generalParams.collaborator_type,
      generalParams.metric,
    ],
    queryFn: () => getGeneralOverview(generalParams),
    enabled: !!selectedEntity?.id,
    staleTime: 5 * 60 * 1000,
  });

  const isInstitution = !selectedEntity?.type || (selectedEntity?.type).toLowerCase() === "institution";

  const {
    data: socialImpactData,
    isLoading: isSocialImpactLoading,
    error: socialImpactError,
    refetch: refetchSocialImpact,
  } = useQuery({
    queryKey: [
      'collab-general-social-impact',
      generalParams.entity_id,
      generalParams.entity_type,
      generalParams.publication_year_start,
      generalParams.publication_year_end,
      generalParams.keywords?.join(','),
      generalParams.keywords_operator,
      generalParams.geography,
      generalParams.collaborator_type,
    ],
    queryFn: () => getGeneralSocialImpact(generalParams),
    enabled: !!selectedEntity?.id && isInstitution,
    staleTime: 5 * 60 * 1000,
  });

  const LOW_PUB_THRESHOLD = 10;
  const pubKpi = overviewData?.kpis?.find(k => (k.label || k.name || "").toLowerCase().includes("publication")) || overviewData?.kpis?.[0];
  const rawTotalPubs = pubKpi?.value ?? 0;
  const parsedPubs = typeof rawTotalPubs === 'string'
    ? parseInt(rawTotalPubs.replace(/,/g, ''), 10)
    : Number(rawTotalPubs);
  const totalPubs = isNaN(parsedPubs) ? 0 : parsedPubs;
  const isLowData = !isOverviewLoading && !!overviewData && totalPubs < LOW_PUB_THRESHOLD;

  useEffect(() => {
    if (initializedRef.current) return;

    const urlEntityId = searchParams.get("entity_id") || searchParams.get("entityId");
    const urlEntityType = searchParams.get("entity_type") || searchParams.get("entityType") || location.state?.entityType;

    if (location.state?.entity) {
      initializedRef.current = true;
      const incomingEntity = location.state.entity;
      const incomingType = incomingEntity.type || "institution";
      navigate(location.pathname, { replace: true, state: null });

      const isNumeric = (val) => val != null && !isNaN(Number(val)) && String(val).trim() !== "";

      if (!isNumeric(incomingEntity.id)) {
        const handleResolved = (data, resolvedType) => {
          if (data) {
            const canonicalId = data.id || incomingEntity.id;
            const isNum = isNumeric(canonicalId);
            const sealUrl = isNum && resolvedType === "institution"
              ? `https://media.academic.online/institutions_images/${canonicalId}/seal.png`
              : isNum && resolvedType === "unit" && (data.institution_id || incomingEntity.institution_id)
              ? `https://media.academic.online/institutions_images/${data.institution_id || incomingEntity.institution_id}/seal.png`
              : undefined;

            setSelectedEntity({
              ...incomingEntity,
              ...data,
              id: canonicalId,
              type: resolvedType,
              name: data.name || data.inst_name || data.display_name || incomingEntity.name,
              photo: incomingEntity.photo || incomingEntity.avatar || data.photo || data.logo_url || data.photo_url || sealUrl,
              avatar: incomingEntity.avatar || incomingEntity.photo || data.avatar || data.photo || data.logo_url || data.photo_url || sealUrl,
              ...(resolvedType === "unit" && {
                institution_id: data.institution_id || incomingEntity.institution_id,
                institution_name: data.institution_name || incomingEntity.institution_name,
                level: data.level || incomingEntity.level,
              }),
            });
            setEntityType(resolvedType);
            setFilters((prev) => ({
              ...prev,
              collaboratorType: resolvedType === "researcher" ? "researcher" : "institution",
            }));
          } else {
            setSelectedEntity(incomingEntity);
          }
          setHasInitialized(true);
        };

        if (incomingType === "researcher") {
          getAuthorProfileSummaryByID(incomingEntity.id, (data) => handleResolved(data, "researcher"));
        } else if (incomingType === "unit") {
          getUnitSummaryById(incomingEntity.id).then((data) => handleResolved(data, "unit"));
        } else {
          getInstitutionDetailSummaryByID(incomingEntity.id, (data) => handleResolved(data, "institution"));
        }
      } else {
        const sealUrl = incomingType === "institution"
          ? `https://media.academic.online/institutions_images/${incomingEntity.id}/seal.png`
          : undefined;

        setSelectedEntity({
          ...incomingEntity,
          photo: incomingEntity.photo || incomingEntity.avatar || sealUrl,
          avatar: incomingEntity.avatar || incomingEntity.photo || sealUrl,
        });
        if (incomingType !== entityType) {
          setEntityType(incomingType);
        }
        setFilters((prev) => ({
          ...prev,
          collaboratorType: incomingType === "researcher" ? "researcher" : "institution",
        }));
        setHasInitialized(true);
      }
    } else if (urlEntityId || location.state?.entityId) {
      const entityId = urlEntityId || location.state.entityId;
      const targetType = urlEntityType || "institution";
      initializedRef.current = true;
      navigate(location.pathname, { replace: true, state: null });

      const handleApiResult = (data, resolvedType) => {
        if (data) {
          const canonicalId = data.id || entityId;
          const isNum = canonicalId != null && !isNaN(Number(canonicalId)) && String(canonicalId).trim() !== "";
          const sealUrl = isNum && resolvedType === "institution"
            ? `https://media.academic.online/institutions_images/${canonicalId}/seal.png`
            : isNum && resolvedType === "unit" && data.institution_id
            ? `https://media.academic.online/institutions_images/${data.institution_id}/seal.png`
            : undefined;

          setSelectedEntity({
            ...data,
            id: canonicalId,
            type: resolvedType,
            name: data.name || data.inst_name || data.display_name,
            photo: data.photo || data.logo_url || data.photo_url || sealUrl,
            avatar: data.photo || data.avatar || data.logo_url || data.photo_url || sealUrl,
            ...(resolvedType === "unit" && {
              institution_id: data.institution_id,
              institution_name: data.institution_name,
              level: data.level,
            }),
          });
          setEntityType(resolvedType);
          setFilters((prev) => ({
            ...prev,
            collaboratorType: resolvedType === "researcher" ? "researcher" : "institution",
          }));
        }
        setHasInitialized(true);
      };

      if (targetType === "researcher") {
        getAuthorProfileSummaryByID(entityId, (data) => handleApiResult(data, "researcher"));
      } else if (targetType === "unit") {
        getUnitSummaryById(entityId).then((data) => handleApiResult(data, "unit"));
      } else {
        getInstitutionDetailSummaryByID(entityId, (data) => handleApiResult(data, "institution"));
      }
    } else if (!isAdminInstLoading) {
      initializedRef.current = true;
      if (adminInstitution) {
        setSelectedEntity({
          ...adminInstitution,
          id: adminInstitution.id,
          type: "institution",
          name: adminInstitution.name,
          photo: adminInstitution.photo || adminInstitution.logo_url || adminInstitution.photo_url || `https://media.academic.online/institutions_images/${adminInstitution.id}/seal.png`,
          avatar: adminInstitution.avatar || adminInstitution.logo_url || adminInstitution.photo_url || `https://media.academic.online/institutions_images/${adminInstitution.id}/seal.png`,
        });
        setEntityType("institution");
        setFilters(prev => ({
          ...prev,
          collaboratorType: "institution"
        }));
      }
      setHasInitialized(true);
    }
  }, [location.state, location.pathname, navigate, entityType, adminInstitution, isAdminInstLoading, searchParams]);

  useEffect(() => {
    setIsDataEmpty(false);
    const mainEl = document.querySelector("main");
    if (mainEl) {
      mainEl.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [filters, selectedEntity]);

  /**
   * Normalize a raw suggest API result into an AntD AutoComplete option.
   * Author and Institution APIs only return { id, name }.
   * Unit API returns { id, name, level, institution_id, institution_name }.
   */
  const normalizeEntityForSearch = useCallback((raw, type) => {
    const entity = {
      id: raw.id,
      name: raw.name,
      type,
      ...(type === "unit" && {
        institution_id: raw.institution_id,
        institution_name: raw.institution_name,
        level: raw.level,
      }),
    };
    return {
      value: raw.name,
      label: raw.name,
      entity, // named 'entity' (not 'data') to avoid conflict with rc-select's FlattenOptionData wrapper
    };
  }, []);

  /**
   * Debounced search handler — called by EntitySelector's AutoComplete onSearch.
   * Picks the correct suggest API based on the current entityType.
   */
  const handleSearch = useCallback((query) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!query || query.length < 2) {
      setSearchOptions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      const callback = (results) => {
        if (!Array.isArray(results)) return;
        setSearchOptions(results.map((r) => normalizeEntityForSearch(r, entityType)));
      };
      if (entityType === "institution") {
        getSuggestSearchInstitutions(query, callback);
      } else if (entityType === "researcher") {
        getSuggestSearchAuthors(query, callback);
      } else if (entityType === "unit") {
        getSuggestSearchUnits(query, callback);
      }
    }, 300);
  }, [entityType, normalizeEntityForSearch]);

  const handleSaveView = () => setIsSaveViewModalOpen(true);

  const handleRestoreView = (savedView) => {
    setSelectedEntity({
      id: savedView.entity_id,
      type: savedView.entity_type,
      name: savedView.entity_name,
      avatar: savedView.entity_avatar,
      photo: savedView.entity_avatar,
      logo: savedView.entity_avatar,
      image: savedView.entity_avatar,
    });
    setFilters({
      yearRange: [savedView.filters.year_range_start, savedView.filters.year_range_end],
      geography: savedView.filters.geography,
      metric: savedView.filters.metric,
      collaboratorType: savedView.filters.collaborator_type,
      subjectArea: savedView.filters.keywords || [],
    });
    const isInstitution = savedView.entity_type === "institution";
    const tabToRestore = savedView.active_tab || "general";
    setActiveTab(isInstitution && tabToRestore === "potential" ? "general" : tabToRestore);
  };

  const handleCreateSavedView = async (name) => {
    await createSavedView({
      name,
      entity_id: selectedEntity.id,
      entity_type: selectedEntity.type,
      entity_name: selectedEntity.name,
      entity_avatar: selectedEntity.photo || selectedEntity.avatar || selectedEntity.logo || selectedEntity.image,
      active_tab: activeTab,
      filters: {
        year_range_start: filters.yearRange?.[0],
        year_range_end: filters.yearRange?.[1],
        geography: filters.geography,
        metric: filters.metric,
        collaborator_type: filters.collaboratorType,
        keywords: filters.subjectArea?.length > 0 ? filters.subjectArea : undefined,
        keywords_operator: filters.keywordsOperator || 'or',
      }
    });
    setRefreshViewsTrigger(prev => prev + 1);
  };
  const handleAddToReport = () => {
    setExportFormat("pdf");
    setIsAddToReportModalOpen(true);
  };
  const handleExportXLSX = () => {
    setExportFormat("xlsx");
    setIsAddToReportModalOpen(true);
  };

  const handleEntityClear = () => {
    setSelectedEntity(null);
    setFilters({
      yearRange: [2014, currentYear],
      subjectArea: [],
      geography: "us",
      metric: "publications",
      collaboratorType: entityType === "researcher" ? "researcher" : "institution",
    });
    setActiveTab("general");
  };

  return (
    <div className="flex flex-col h-full min-h-screen bg-gray-50">
      <div className="flex flex-col flex-1 p-6 gap-6">
        <PageHeader />

        {!selectedEntity && (!hasInitialized || isAdminInstLoading) ? (
          <CollaborationSkeleton />
        ) : (
          <>
            <EntitySelector
              entityType={entityType}
              onEntityTypeChange={(type) => {
                setEntityType(type);
                setSearchOptions([]);
                setFilters(prev => ({
                  ...prev,
                  collaboratorType: type === "researcher" ? "researcher" : "institution"
                }));
              }}
              selectedEntity={selectedEntity}
              onEntitySelect={(entity) => {
                setSelectedEntity(entity);
                setSearchOptions([]);

                // Sync the entityType if selected from recent searches
                if (entity.type && entity.type !== entityType) {
                  setEntityType(entity.type);
                }

                // Always ensure the collaboratorType filter matches the chosen entity
                setFilters(prev => ({
                  ...prev,
                  collaboratorType: entity.type === "researcher" ? "researcher" : "institution"
                }));

                if (entity.type === "institution") {
                  setActiveTab((prev) => (prev === "potential" ? "general" : prev));
                }
              }}
              onEntityClear={handleEntityClear}
              searchOptions={searchOptions}
              onSearch={handleSearch}
            />

            {!selectedEntity ? (
              <div className="flex flex-col items-center justify-center text-center p-16 bg-white border border-dashed border-gray-300 rounded-lg flex-1">
                <Search className="text-gray-300 w-10 h-10 mb-4" />
                <div className="max-w-md w-full mx-auto">
                  <h2 className="text-lg font-medium text-gray-600 mb-2 tracking-tight">Select an entity to get started</h2>
                  <p className="text-sm text-gray-500 m-0 leading-relaxed">
                    Search for an institution, unit, or researcher, or paste a profile URL
                    to begin exploring collaboration data.
                  </p>

                  <div className="mt-8 w-full flex flex-col p-8 bg-gray-50 rounded-xl border border-dashed border-gray-300 mx-auto">
                    <div className="text-gray-600 text-xs uppercase tracking-wider font-semibold text-center mb-6">
                      Or explore your saved views
                    </div>
                    <div className="w-full">
                      <SavedViewsList
                        onRestoreView={handleRestoreView}
                        refreshTrigger={refreshViewsTrigger}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row items-start flex-1 gap-6">
                <div className="w-full lg:w-[280px] flex-shrink-0 lg:sticky lg:top-0 h-max max-h-screen overflow-y-auto bg-white border border-gray-300 rounded-lg">
                  <FiltersRow filters={filters} onFilterChange={setFilters} entityType={selectedEntity?.type || "institution"} />
                </div>

                {isOverviewLoading && !overviewData ? (
                  <div className="flex-1 min-w-0 flex flex-col bg-white border border-gray-300 rounded-lg min-h-[500px] items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : isLowData ? (
                  <div className="flex-1 min-w-0">
                    <InsufficientDataNotice
                      pubCount={totalPubs}
                      entity={selectedEntity}
                      filters={filters}
                      setFilters={setFilters}
                    />
                  </div>
                ) : isDataEmpty && filters.subjectArea?.length > 0 ? (
                  <div className="flex-1 min-w-0">
                    <EmptyStateSuggest
                      entity={selectedEntity}
                      filters={filters}
                      setFilters={setFilters}
                    />
                  </div>
                ) : (
                  <div className="flex-1 min-w-0 flex flex-col bg-white border border-gray-300 rounded-lg min-h-[500px]">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-w-0">
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-gray-300 px-6 pt-2">
                        <TabsList className="bg-transparent h-12 p-0 m-0 justify-start self-end">
                          <TabsTrigger value="general" className="data-[state=active]:border-b-2 data-[state=active]:border-explore data-[state=active]:text-explore data-[state=active]:shadow-none rounded-none bg-transparent px-4 h-full border-b-2 border-transparent">General</TabsTrigger>
                          <TabsTrigger value="current" className="data-[state=active]:border-b-2 data-[state=active]:border-explore data-[state=active]:text-explore data-[state=active]:shadow-none rounded-none bg-transparent px-4 h-full border-b-2 border-transparent">Current Collaborations</TabsTrigger>
                          {selectedEntity?.type !== "institution" && (
                            <TabsTrigger value="potential" className="data-[state=active]:border-b-2 data-[state=active]:border-explore data-[state=active]:text-explore data-[state=active]:shadow-none rounded-none bg-transparent px-4 h-full border-b-2 border-transparent">Potential Collaborations</TabsTrigger>
                          )}
                        </TabsList>

                        <div className="flex items-center gap-2 pb-2 mt-2 md:mt-0">
                          <SavedViewsDropdown
                            onRestoreView={handleRestoreView}
                            refreshTrigger={refreshViewsTrigger}
                            onSaveCurrentView={handleSaveView}
                            disableSave={!selectedEntity}
                          />
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="outline" size="sm" onClick={handleExportXLSX} className="flex items-center gap-2 text-gray-700">
                                  <Table size={16} /> Export XLSX
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Export data to XLSX</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="outline" size="sm" onClick={handleAddToReport} className="flex items-center gap-2 text-gray-700">
                                  <FilePlus size={16} /> Add to Report
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Add this view to a report</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      <div className="flex-1 overflow-auto p-6">
                        <TabsContent forceMount={true} value="general" className="mt-0 h-full data-[state=inactive]:hidden">
                          <TabGeneral
                            filters={filters}
                            setFilters={setFilters}
                            onDataEmpty={setIsDataEmpty}
                            entity={selectedEntity}
                            isActive={activeTab === "general"}
                            overviewData={overviewData}
                            isOverviewLoading={isOverviewLoading}
                            overviewError={overviewError}
                            socialImpactData={socialImpactData}
                            isLoadingSocialImpact={isSocialImpactLoading}
                            socialImpactError={socialImpactError ? (socialImpactError?.response?.data?.detail?.errors?.[0]?.msg || socialImpactError?.response?.data?.message || socialImpactError?.message || "Failed to load social impact data.") : null}
                            refetchSocialImpact={refetchSocialImpact}
                          />
                        </TabsContent>
                        <TabsContent forceMount={true} value="current" className="mt-0 h-full data-[state=inactive]:hidden">
                          <TabCurrent filters={filters} setFilters={setFilters} onDataEmpty={setIsDataEmpty} entity={selectedEntity} isActive={activeTab === "current"} />
                        </TabsContent>
                        {selectedEntity?.type !== "institution" && (
                          <TabsContent forceMount={true} value="potential" className="mt-0 h-full data-[state=inactive]:hidden">
                            <TabPotential filters={filters} setFilters={setFilters} onDataEmpty={setIsDataEmpty} entity={selectedEntity} isActive={activeTab === "potential"} />
                          </TabsContent>
                        )}
                      </div>
                    </Tabs>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <AddToReportModal
        open={isAddToReportModalOpen}
        onClose={() => setIsAddToReportModalOpen(false)}
        entity={selectedEntity}
        filters={filters}
        exportFormat={exportFormat}
      />

      <SaveViewModal
        open={isSaveViewModalOpen}
        onClose={() => setIsSaveViewModalOpen(false)}
        onSave={handleCreateSavedView}
        entity={selectedEntity}
        filters={filters}
        activeTab={activeTab}
      />
    </div>
  );
};

export default Collaborations;
